// cacheFreshness.js — decides when the velocity cache must be fully re-fetched
// from Jira instead of incrementally merged.
//
// WHY THIS EXISTS
// The dashboard's default refresh is INCREMENTAL: it only re-fetches the last
// ~3 sprints and merges everything older from the browser's localStorage cache
// (see fetchData / mergeDashboardData in App.jsx). That means a sprint is only
// ever refreshed while it sits inside that 3-sprint window. A sprint that was
// FIRST cached while it was still the current, not-yet-closed cycle is frozen
// at its partial value — often ~0 velocity, because closed-sprint velocity only
// counts sprints Jira has marked closed. Once that sprint ages out of the
// incremental window, the stale (near-zero) value never heals, so its bar on
// the Velocity Trend chart reads "no data" forever on that one browser while
// fresh data everywhere else is correct.
//
// THE FIX
// Bound how long any cached sprint can stay stale by forcing a FULL refresh
// (fetchData('all'), which replaces the entire cache) whenever the last full
// sync is missing or older than FULL_SYNC_MAX_AGE_MS. Incremental refreshes
// still run in between for speed; this just guarantees the whole window is
// rebuilt from Jira at least this often, healing any frozen sprint.
//
// Dependency-free on purpose so cacheFreshness.test.mjs runs under plain node
// in CI (same pattern as server/sprint-math.js).

// Longest a cached sprint may stay stale before a full refresh is forced.
// 3 days: short enough that a sprint frozen at its just-closed partial value
// self-heals within a couple of viewing sessions, long enough that the common
// case (same-day revisits) still uses the fast incremental path.
export const FULL_SYNC_MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000

// True when the workspace's cache should be FULLY re-fetched from Jira rather
// than incrementally merged. Fails safe toward a full refresh: a missing or
// unparseable timestamp returns true (a full fetch is always correct, just
// slower). `now` is injected for testability.
//
// lastFullSyncIso — the ISO string persisted at jiraDashboard_lastFullSync_<ws>
//   the last time a full (no-sinceDate) fetch completed; null if never.
export function shouldForceFullSync(lastFullSyncIso, now = Date.now()) {
    if (!lastFullSyncIso) return true
    const t = new Date(lastFullSyncIso).getTime()
    if (!Number.isFinite(t)) return true
    // Future timestamps (clock skew) are treated as fresh — age is negative,
    // which is < max age, so incremental is allowed.
    return now - t > FULL_SYNC_MAX_AGE_MS
}
